/**
 * Per-frame camera motion for in-browser export, following `backend/video_motion.py`
 * (`apply_motion_to_frames`, `apply_audio_motion_timeline_to_frames`).
 */
import { EDITOR_ANIMATION_SPEED_MAX } from '@/lib/editorAnimationSettings';
import { motionProgressWithSpeed } from '@/lib/motionProgress';
import { sampleAudioMotionTimeline } from '@/lib/songAudioMotion';
import {
  clampMotionStrength,
  computeMotionTransform,
  normalizeMotionPreset,
} from '@/lib/videoMotionPresets';

const IDENTITY = { scale: 1, tx: 0, ty: 0, rotate: 0 };

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function hashNoise(i, salt) {
  const x = Math.sin((i + 1) * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

/** Preset (Ken Burns etc.) transform for output frame `j` of a `count`-frame clip. */
export function presetMotionAt(item, j, count) {
  const preset = normalizeMotionPreset(item?.motion_preset);
  if (preset === 'none') return null;
  const speed = Math.max(0, Math.min(EDITOR_ANIMATION_SPEED_MAX, num(item?.editor_animation_speed) || 1));
  let strength = clampMotionStrength(item?.motion_strength);
  if (speed > 1) strength = Math.min(0.45, strength * (1 + speed * 0.08));
  const progress = motionProgressWithSpeed(j / Math.max(1, count - 1), speed);
  return computeMotionTransform(preset, progress, strength);
}

/** Beat-sync transform at output frame `j` (clip-local time `offset + j / fps`). */
export function audioMotionAt(item, j, fps, frameW = 1280, frameH = 720) {
  const timeline = item?.audio_motion_timeline;
  if (!timeline?.keyframes?.length) return null;
  const speed = Math.max(0, Math.min(1, num(item?.song_animation_speed) || 0.38));
  if (speed <= 0) return IDENTITY;
  const floatSoft = speed < 0.05 ? Math.max(0, Math.min(1, (0.05 - speed) / 0.0495)) : 0;
  const calm = 1 - floatSoft * 0.94;
  const t = Math.max(0, num(item?.audio_motion_time_offset)) + j / Math.max(1, fps);
  const params = sampleAudioMotionTimeline(timeline, t, speed);
  if (!params) return IDENTITY;

  const intensity = num(params._beatSyncIntensity, 1) || 1;
  const drive = num(params.motion_intensity, 0.2) || 0.2;
  const driveThresh = Math.max(0.22, 0.42 - intensity * 0.14);
  const presetBlend = 0.28 * (0.12 + Math.min(1, intensity / 3.5) * 0.55);
  let scale = Math.max(1, Math.min(2.2, num(params.scale, 1) || 1));
  let tx = num(params.pan_x);
  let ty = num(params.pan_y);
  let rotate = num(params.rotate_deg);
  const shakeScale = Math.max(0, Math.min(1, (drive - driveThresh) / 0.35));
  const shake = num(params.shake_px) * shakeScale * calm * (0.55 + intensity * 0.35);
  if (shake > 0.5) {
    tx += ((hashNoise(j, 1) - 0.5) * shake) / Math.max(1, frameW);
    ty += ((hashNoise(j, 2) - 0.5) * shake) / Math.max(1, frameH);
  }
  const base = computeMotionTransform(
    params.base_preset || 'slow_drift',
    num(params.energy, 0.5),
    clampMotionStrength(params.base_strength) * 0.85,
  );
  scale = Math.max(scale, base.scale * 0.75 + scale * 0.25);
  tx += base.tx * presetBlend;
  ty += base.ty * presetBlend;
  rotate += base.rotate * (0.28 * calm);
  return { scale, tx, ty, rotate };
}

/** Beat-sync wins over the preset, as on the server. */
export function clipMotionAt(item, j, count, fps, frameW, frameH) {
  return audioMotionAt(item, j, fps, frameW, frameH) || presetMotionAt(item, j, count);
}
